import { defineStore } from 'pinia'
import {
  MitoVault,
  MitoHolders,
  TokenStatic,
  MitoTransfer,
  MitoPortfolio
} from '@injectivelabs/sdk-ts'
import { mitoApi } from '@shared/Service'
import {
  streamVault,
  cancelVaultStream,
  streamVaultTransfers,
  streamHolderSubscription
} from '@/store/mito/stream'
import {
  redeem,
  subscribeToSpotVault,
  subscribeToDerivativeVault
} from '@/store/mito/message'
import {
  fetchVaultConfig,
  resetVaultConfig,
  instantiateCPMMVault,
  instantiateSpotVault,
  updateCPMMVaultConfig,
  updateSpotVaultConfig,
  updateOffChainVaultConfig,
  instantiateDerivativeVault,
  updateDerivativeVaultConfig,
  instantiatePermissionlessCPMMVault
} from '@/store/mito/config'
import { STAKING_CONTRACT_ADDRESS } from '@/app/utils/constant'
import { UiMitoTransformer } from '@/app/client/transformers/mito'
import {
  UiVault,
  VaultConfig,
  UiSubscription,
  UIPriceSnapShot,
  VaultContractType
} from '@/types'

async function fetchAllVaults(pageIndex?: number, allVaults: MitoVault[] = []) {
  const { vaults, pagination } = await mitoApi.fetchVaults({ pageIndex })

  allVaults = [...allVaults, ...vaults]

  const nextPageIndex = pageIndex === undefined ? 1 : pageIndex + 1

  if (Number(allVaults.length) < Number(pagination?.total || 0)) {
    return fetchAllVaults(nextPageIndex, allVaults)
  }

  return allVaults
}

type MitoStoreState = {
  vaults: UiVault[]
  vaultConfig: VaultConfig
  totalTransfers: number
  transfers: MitoTransfer[]
  totalVaultHolders: number
  vaultHolders: MitoHolders[]
  holderPortfolio: MitoPortfolio
  tvlChartData: UIPriceSnapShot[]
  userHasCPMMVaultAllowance: boolean
  lpTokenChartData: UIPriceSnapShot[]
  holderSubscriptions: UiSubscription[]
  tokensPriceSparkLine: Record<string, UIPriceSnapShot[]>
}

const initialStateFactory = (): MitoStoreState => ({
  vaults: [],
  transfers: [],
  tvlChartData: [],
  vaultHolders: [],
  totalTransfers: 0,
  totalVaultHolders: 0,
  lpTokenChartData: [],
  holderSubscriptions: [],
  tokensPriceSparkLine: {},
  vaultConfig: {} as VaultConfig,
  userHasCPMMVaultAllowance: false,
  holderPortfolio: {} as MitoPortfolio
})

export const useMitoStore = defineStore('mito', {
  state: (): MitoStoreState => initialStateFactory(),
  getters: {
    masterContractAddress: (state: MitoStoreState) => {
      const vault = state.vaults.find(
        (vault) => vault.vaultType !== VaultContractType.OffChain
      )

      return vault?.masterContractAddress
    },

    cpmmCodeId: (state: MitoStoreState) => {
      const cpmmVault = state.vaults.find(
        (vault) => vault.vaultType === VaultContractType.CPMM
      )

      return cpmmVault?.codeId
    },

    tradableTokens: (state: MitoStoreState) => {
      const tradableTokens = state.vaults.reduce(
        (tokens, vault) => [
          ...tokens,
          vault.market.baseToken,
          vault.market.quoteToken
        ],
        [] as TokenStatic[]
      )

      return [
        ...new Map(tradableTokens.map((token) => [token.denom, token])).values()
      ]
    }
  },

  actions: {
    redeem,
    streamVault,
    resetVaultConfig,
    fetchVaultConfig,
    cancelVaultStream,
    updateCPMMVaultConfig,
    updateSpotVaultConfig,
    streamVaultTransfers,
    subscribeToSpotVault,
    instantiateSpotVault,
    instantiateCPMMVault,
    updateOffChainVaultConfig,
    streamHolderSubscription,
    subscribeToDerivativeVault,
    instantiateDerivativeVault,
    updateDerivativeVaultConfig,
    instantiatePermissionlessCPMMVault,

    async fetchVault(contractAddress: string) {
      const mitoStore = useMitoStore()

      const vault = await mitoApi.fetchVault({ contractAddress })

      const uiVault = UiMitoTransformer.vaultToUiVault(vault)

      const filteredVaultList = mitoStore.vaults.filter(
        ({ contractAddress }) => contractAddress !== uiVault?.contractAddress
      )

      mitoStore.$patch({
        vaults: [...filteredVaultList, uiVault].filter((vault) => vault)
      })
    },

    async fetchVaults() {
      const mitoStore = useMitoStore()

      const vaults = await fetchAllVaults()

      const filteredVaults = vaults.filter(
        (vault) => vault && vault.subaccountInfo?.subaccountId
      ) as MitoVault[]

      const uiVaults = filteredVaults
        .map(UiMitoTransformer.vaultToUiVault)
        .filter((vault) => vault) as UiVault[]

      const uiVaultsWithChartData = uiVaults.map((uiVault) => {
        const mitoVaultFromStore = mitoStore.vaults.find(
          (vault) => vault.contractAddress === uiVault.contractAddress
        )

        // override with store data - fetchVaults api returns partial tvlChanges and profits
        const tvlChanges = mitoVaultFromStore
          ? {
              ...(uiVault.tvlChanges
                ? {
                    ...mitoVaultFromStore.tvlChanges,
                    ...JSON.parse(JSON.stringify(uiVault.tvlChanges))
                  }
                : mitoVaultFromStore.tvlChanges)
            }
          : uiVault.tvlChanges

        const profits = mitoVaultFromStore
          ? {
              ...(uiVault.profits
                ? {
                    ...mitoVaultFromStore.profits,
                    ...JSON.parse(JSON.stringify(uiVault.profits))
                  }
                : mitoVaultFromStore.profits)
            }
          : uiVault.profits

        return {
          ...uiVault,
          profits,
          tvlChanges
        }
      })

      mitoStore.$patch({
        vaults: uiVaultsWithChartData
      })
    },

    async fetchTokenPriceChart({
      vaultAddress,
      from
    }: {
      vaultAddress: string
      from?: string
    }) {
      const mitoStore = useMitoStore()

      const tokenPriceSparkLine = await mitoApi.fetchLpTokenPriceChart({
        from,
        vaultAddress
      })

      mitoStore.$patch({
        tokensPriceSparkLine: {
          [vaultAddress]: tokenPriceSparkLine
        }
      })
    },

    async fetchTVLPriceChart({ vaultAddress }: { vaultAddress: string }) {
      const mitoStore = useMitoStore()

      const tvlChartData = await mitoApi.fetchTVLChartRequest({
        vaultAddress
      })

      mitoStore.$patch({
        tvlChartData: tvlChartData.map(
          UiMitoTransformer.tvlPriceSnapshotToUiPriceSnapshot
        )
      })
    },

    async fetchHolderSubscriptions() {
      const mitoStore = useMitoStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.injectiveAddress) {
        return
      }

      const { subscriptions } = await mitoApi.fetchVaultsByHolderAddress({
        holderAddress: sharedWalletStore.injectiveAddress
      })

      const uiHolderSubscriptions = subscriptions
        .map(UiMitoTransformer.subscriptionToUiSubscription)
        .filter((subscription) => subscription)

      mitoStore.$patch({
        holderSubscriptions: uiHolderSubscriptions
      })
    },

    async fetchLpHoldersCount(vaultAddress: string) {
      const mitoStore = useMitoStore()

      const { pagination } = await mitoApi.fetchLPHolders({
        vaultAddress,
        stakingContractAddress: STAKING_CONTRACT_ADDRESS
      })

      mitoStore.$patch({
        totalVaultHolders: (pagination?.total || 0) as number
      })
    },

    async fetchLpHolders({
      skip,
      limit,
      vaultAddress
    }: {
      limit?: number
      skip?: number
      vaultAddress: string
    }) {
      const mitoStore = useMitoStore()

      const { holders, pagination } = await mitoApi.fetchLPHolders({
        skip,
        limit,
        vaultAddress,
        stakingContractAddress: STAKING_CONTRACT_ADDRESS
      })

      if (!skip) {
        mitoStore.$patch({
          totalVaultHolders: (pagination?.total || 0) as number
        })
      }

      mitoStore.$patch({
        vaultHolders: holders
      })
    },

    async fetchHolderPortfolio() {
      const mitoStore = useMitoStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.injectiveAddress) {
        return
      }

      mitoStore.$patch({
        holderPortfolio: await mitoApi.fetchHolderPortfolio({
          holderAddress: sharedWalletStore.injectiveAddress,
          stakingContractAddress: STAKING_CONTRACT_ADDRESS
        })
      })
    },

    async fetchTransfers({
      limit,
      toNumber,
      vaultAddress,
      accountAddress
    }: {
      limit: number
      toNumber?: number
      vaultAddress?: string
      accountAddress?: string
    }) {
      const mitoStore = useMitoStore()

      const { transfers, pagination } = await mitoApi.fetchTransferHistory({
        limit,
        toNumber,
        vault: vaultAddress,
        account: accountAddress
      })

      mitoStore.$patch({
        transfers
      })

      if (!toNumber) {
        mitoStore.$patch({
          totalTransfers: Number(pagination?.total) || 0
        })
      }
    },

    async queryUserCPMMVaultAllowance() {
      const mitoStore = useMitoStore()
      const launchpadStore = useLaunchpadStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!mitoStore.cpmmCodeId) {
        return
      }

      const exist = await launchpadStore.queryUserHasOneTimeAllowance({
        codeId: mitoStore.cpmmCodeId,
        address: sharedWalletStore.injectiveAddress,
        masterContractAddress: mitoStore.masterContractAddress as string
      })

      mitoStore.$patch({
        userHasCPMMVaultAllowance: exist
      })
    },

    reset() {
      const mitoStore = useMitoStore()

      const initialState = initialStateFactory()

      mitoStore.$patch({
        ...initialState
      })
      mitoStore.holderPortfolio = initialState.holderPortfolio
    }
  }
})
